import React from 'react'
import { withRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { getTrialInformation, getUpgraded } from 'ducks/apps'
import {
  showModal as showUpgradeModal,
  setPaymentRouteType,
} from 'ducks/trialWarning'
import { showModal, INTEGRATION_TRIAL_MODAL } from 'ducks/editor/modals'
import { getApiKey, createApiKey } from 'ducks/apikeys'

import Button from '../Button'
import EmptyState from '../EmptyState'

import './IntegrationPackTrial.scss'

const UPGRADE_TEXT = 'Upgrade Now'

// The same (for now) regardless of which phase of the free trial a user is in
const LOCATION_PAYWALL_TEXT = {
  header: 'Upgrade to Use Location Features',
  body: 'Build apps that use addresses and user locations with our Google Maps Integration.',
  button: UPGRADE_TEXT,
}

const generateText = days => ({
  externalCollection: {
    before: {
      header: 'Connect to External Data Sources',
      body: `Integrate your app's database with external sources like Airtable, Google, or any custom API.`,
      button: 'Start Integration Trial',
    },
    during: {
      header: `Integration Pack Trial | ${days} Left`,
      body: 'Upgrade now to make sure all of your external collections stay up and running.',
      button: UPGRADE_TEXT,
    },
    after: {
      header: 'Your Integration Pack Trial Is Over',
      body: 'Your external collections have been paused. Upgrade now to continue using them.',
      button: UPGRADE_TEXT,
    },
  },
  customAction: {
    during: {
      header: `Integration Pack Trial | ${days} Left`,
      body: 'Upgrade now to make sure all of your custom actions stay up and running.',
      button: UPGRADE_TEXT,
    },
    after: {
      header: 'Your Integration Pack Trial Is Over',
      body: 'This custom action has been paused. Upgrade now to get access to custom actions.',
      button: UPGRADE_TEXT,
    },
  },
  api: {
    before: {
      header: 'Get Access To Your App’s API Key',
      body: 'Use your App’s API to send & receive custom API requests.',
      button: 'Start Integration Pack Trial',
    },
    during: {
      header: `Integration Pack Trial | ${days} Left`,
      body: 'Upgrade now to continue to send & receive custom API requests.',
      button: UPGRADE_TEXT,
    },
    after: {
      header: 'Your Integration Pack Trial Is Over',
      body: 'Access to your App’s API has been paused. Upgrade now to continue using it.',
      button: UPGRADE_TEXT,
    },
  },
  geolocation: {
    before: LOCATION_PAYWALL_TEXT,
    during: LOCATION_PAYWALL_TEXT,
    after: LOCATION_PAYWALL_TEXT,
  },
})

const IntegrationPackTrial = ({ appId, type, history }) => {
  const dispatch = useDispatch()

  const { isPaid, days, trialState, appApiKey } = useSelector(state => ({
    appApiKey: getApiKey(state, appId),
    ...getTrialInformation(state, appId),
    isPaid: getUpgraded(state, appId),
  }))

  if (isPaid || (type === 'customAction' && trialState === 'before')) {
    return null
  }

  const { header, body, button } = generateText(days)[type][trialState]

  const openUpgradeModal = async () => {
    await dispatch(setPaymentRouteType('upgrade'))
    await dispatch(showUpgradeModal())
  }

  const openIntegrationModal = async () => {
    await dispatch(showModal(INTEGRATION_TRIAL_MODAL, { type, appId }))

    if (type === 'externalCollection') {
      history.push(`/apps/${appId}/data/new-external-collection`)
    } else if (type === 'api') {
      if (!appApiKey) {
        await createApiKey(appId)
      }

      window.open(`/apps/${appId}/api-docs`)
    }
  }

  const handleTrial = async () => {
    try {
      if (button === UPGRADE_TEXT) {
        await openUpgradeModal()
      } else {
        await openIntegrationModal()
      }
    } catch (err) {
      // modal closed
      console.error("modal closed, here's the error:", err)
    }
  }

  return (
    <EmptyState
      bordered
      className={`data-upgrade-prompt ${type}-${trialState}-integration-trial-prompt ${
        appApiKey ? 'integration-trial-with-api-key' : ''
      }`}
    >
      <h2>{header}</h2>
      <p>{body}</p>
      <Button small orange onClick={handleTrial}>
        {button}
      </Button>
    </EmptyState>
  )
}

export default withRouter(IntegrationPackTrial)
